/**
 * The shop checkout's standard look (the Prints mockup's checkout:
 * docs/mockups/telify-web/dana-wilcher/prints.html, `.co`, `.fs`, `.fld`,
 * `.ship-opt`, `.co-sum`), in theme tokens: the form on the left, the order
 * summary held still on the right; on a phone the summary folds above the form
 * behind Show your order. The payment is its own piece, passed in by the page.
 * Draws the behavior half's state and calls its actions; holds no rule.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { partsOf, pieceRoot } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import { onLink } from '../../ui/onLink';
import { PrintPicture } from '../../ui/PrintPicture';
import { contract, type ShopCheckoutField } from './contract';
import type { ShopCheckoutRuntime } from './useShopCheckout';
import css from './ShopCheckout.module.css';

const part = partsOf(contract);

export interface ShopCheckoutSlots {
  /** The payment, once the order is placed: the `shop-payment-step` piece. */
  readonly payment?: ReactNode;
}

interface FieldProps {
  readonly runtime: ShopCheckoutRuntime;
  readonly name: ShopCheckoutField;
  readonly label: string;
  readonly autoComplete: string;
  readonly type?: string;
  readonly placeholder?: string;
  readonly inputMode?: 'text' | 'email' | 'numeric';
  readonly options?: readonly { readonly value: string; readonly label: string }[];
}

function Field({ runtime, name, label, autoComplete, type = 'text', placeholder, inputMode, options }: FieldProps) {
  const id = useId();
  const error = runtime.state.errors[name];
  const common = {
    id,
    name,
    autoComplete,
    value: runtime.state.form[name] ?? '',
    'aria-invalid': error ? (true as const) : undefined,
    'aria-describedby': error ? `${id}-error` : undefined,
  };
  return (
    <div className={`${css.fld} ${error ? css.bad : ''}`} {...part('field')}>
      <label htmlFor={id}>{label}</label>
      {options ? (
        <select {...common} onChange={(e) => runtime.actions.setField(name, e.target.value)}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          {...common}
          type={type}
          inputMode={inputMode}
          placeholder={placeholder}
          onChange={(e) => runtime.actions.setField(name, e.target.value)}
        />
      )}
      {error && (
        <span className={css.err} id={`${id}-error`}>
          {error}
        </span>
      )}
    </div>
  );
}

export function ShopCheckoutView({ payment, ...runtime }: ShopCheckoutRuntime & ShopCheckoutSlots) {
  const { state, actions } = runtime;
  const form = useRef<HTMLFormElement>(null);
  const [open, setOpen] = useState(false);

  // Something missing: the first field that needs it takes the focus.
  useEffect(() => {
    if (state.attempt === 0) return;
    const first = form.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    first?.focus();
    first?.scrollIntoView({ block: 'center' });
  }, [state.attempt]);

  const us = state.form.country === 'US';
  const { shipping } = state;
  return (
    <div {...pieceRoot(contract)} {...part('page')}>
      <div className={css.head}>
        <h1 className={css.title} {...part('title')}>
          Checkout
        </h1>
        <a className={css.back} href="/shop/bag" onClick={onLink(actions.back)} {...part('back')}>
          <Icon name="back" small />
          Back to your bag
        </a>
      </div>

      <div className={css.co}>
        <form ref={form} className={css.form} noValidate onSubmit={(e) => e.preventDefault()}>
          {state.session === 'signedIn' && state.client ? (
            <div className={`${css.tg} ${css.tgIn}`} {...part('telegram')}>
              <p>
                <Icon name="check" />
                {`Signed in with Telegram as ${state.client}. ${state.filledIn ? 'Your address is filled in.' : 'We’ll keep your address for next time.'}`}
              </p>
            </div>
          ) : (
            state.session === 'signedOut' &&
            state.canSignIn && (
              <div className={css.tg} {...part('telegram')}>
                <p>
                  <Icon name="tg" />
                  Have Telegram? Sign in and your address fills in next time.
                </p>
                <button type="button" className={`${shared.btn} ${shared.btnOutline} ${css.sm}`} onClick={actions.signIn} {...part('signIn')}>
                  Sign in with Telegram
                </button>
              </div>
            )
          )}

          <fieldset className={css.fs} {...part('email')}>
            <legend>{state.asksBuyerName ? 'About you' : 'Your email'}</legend>
            {state.asksBuyerName && <Field runtime={runtime} name="buyerName" label="Your name" autoComplete="name" />}
            <Field runtime={runtime} name="email" label="Email" type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" />
            <p className={css.hint}>Your receipt goes here, then an email with tracking when it ships.</p>
          </fieldset>

          <fieldset className={css.fs} {...part('shipTo')}>
            <legend>Ship to</legend>
            {state.savedAddress ? (
              <div className={css.saved} {...part('savedAddress')}>
                <p>
                  {state.savedAddress.name}
                  <br />
                  <span className={css.sub}>{state.savedAddress.line}</span>
                </p>
                <button type="button" className={css.textlink} onClick={actions.changeAddress} {...part('change')}>
                  Change
                </button>
              </div>
            ) : (
              <>
                <Field runtime={runtime} name="name" label="Full name" autoComplete="name" />
                <Field
                  runtime={runtime}
                  name="country"
                  label="Country"
                  autoComplete="country"
                  options={state.countries.map((c) => ({ value: c.code, label: c.name }))}
                />
                <Field runtime={runtime} name="line1" label="Street address" autoComplete="address-line1" />
                <Field runtime={runtime} name="line2" label="Apartment, suite or unit (optional)" autoComplete="address-line2" />
                <div className={css.row3}>
                  <Field runtime={runtime} name="city" label="City" autoComplete="address-level2" />
                  {us ? (
                    <Field
                      runtime={runtime}
                      name="state"
                      label="State"
                      autoComplete="address-level1"
                      options={[{ value: '', label: 'Choose' }, ...state.states.map((s) => ({ value: s, label: s }))]}
                    />
                  ) : (
                    <Field
                      runtime={runtime}
                      name="state"
                      label={state.form.country === 'CA' ? 'Province' : state.form.country === 'AU' ? 'State or territory' : 'State or province (optional)'}
                      autoComplete="address-level1"
                    />
                  )}
                  <Field
                    runtime={runtime}
                    name="postalCode"
                    label={us ? 'ZIP code' : 'Postal code'}
                    autoComplete="postal-code"
                    inputMode={us ? 'numeric' : 'text'}
                  />
                </div>
              </>
            )}
          </fieldset>

          <fieldset className={css.fs}>
            <legend>Shipping</legend>
            <div className={`${css.ship} ${shipping.status === 'priced' ? css.shipOn : ''}`} {...part('shipping')}>
              {shipping.status === 'priced' ? (
                <>
                  <div>
                    <p>Standard shipping</p>
                    {shipping.arrives && <p className={css.sub}>{shipping.arrives}</p>}
                    {shipping.importTaxes && <p className={css.sub}>Import taxes may be charged on arrival.</p>}
                  </div>
                  <b>{shipping.price}</b>
                </>
              ) : shipping.status === 'refused' ? (
                <p className={css.refused} role="alert">
                  {shipping.reason}
                </p>
              ) : (
                <p className={css.sub}>{shipping.status === 'working' ? 'Working out shipping…' : 'Enter your address to see shipping.'}</p>
              )}
            </div>
          </fieldset>

          <fieldset className={css.fs} {...part('payment')}>
            <legend>Payment</legend>
            {state.expired && (
              <p className={css.alert} role="alert" {...part('expired')}>
                This checkout has expired. Review your order and try again.
              </p>
            )}
            {state.problem && (
              <p className={css.alert} role="alert" {...part('problem')}>
                {state.problem}
              </p>
            )}
            {state.step === 'payment' && payment ? (
              payment
            ) : (
              <>
                <label className={css.tick} {...part('adult')}>
                  <input type="checkbox" checked={state.adultConfirmed} onChange={(e) => actions.setAdult(e.target.checked)} />
                  <span>I'm 18 or over, and this order ships in an adult's name.</span>
                </label>
                <button
                  type="button"
                  className={`${shared.btn} ${shared.btnPrimary} ${css.wide}`}
                  disabled={state.step === 'placing' || !state.adultConfirmed || shipping.status === 'refused' || shipping.status === 'working'}
                  onClick={actions.continue}
                  {...part('continue')}
                >
                  {state.step === 'placing' ? 'Placing your order…' : 'Continue to payment'}
                </button>
              </>
            )}
          </fieldset>
        </form>

        <aside className={`${css.sum} ${open ? css.open : ''}`} aria-label="Your order" {...part('summary')}>
          <button type="button" className={css.toggle} aria-expanded={open} onClick={() => setOpen((o) => !o)} {...part('summaryToggle')}>
            <span>{open ? 'Hide your order' : 'Show your order'}</span>
            <span>{state.total ?? state.subtotal}</span>
          </button>
          <div className={css.sumBody}>
            <h2>Your order</h2>
            <div className={css.items}>
              {state.items.map((item) => (
                <div key={item.optionId} className={css.item} {...part('item')}>
                  <span className={css.thumb}>
                    {item.imageUrl && <PrintPicture url={item.imageUrl} alt="" size={item.size} canvas={item.canvas} />}
                    {item.quantity > 1 && <span className={css.q}>{item.quantity}</span>}
                  </span>
                  <div>
                    <div className={css.itemName} {...part('itemName')}>
                      {item.name}
                    </div>
                    {item.option && <div className={css.sub}>{item.option}</div>}
                  </div>
                  <div>{item.total}</div>
                </div>
              ))}
            </div>
            <div className={css.line} {...part('row')}>
              <span className={css.sub}>Subtotal</span>
              <span>{state.subtotal}</span>
            </div>
            <div className={css.line} {...part('row')}>
              <span className={css.sub}>Shipping</span>
              <span className={state.total ? '' : css.sub}>{state.shippingRow}</span>
            </div>
            {state.tax && (
              <div className={css.line} {...part('row')}>
                <span className={css.sub}>Sales tax</span>
                <span>{state.tax}</span>
              </div>
            )}
            {state.total && (
              <div className={`${css.line} ${css.big}`} {...part('total')}>
                <span>Total</span>
                <span>{state.total}</span>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
